import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { Bouton } from './composants.tsx';

/**
 * Retours d'action : notifications et confirmations.
 *
 * ── Pourquoi remplacer `window.confirm()` ───────────────────────────────────
 * La boîte native bloque l'onglet entier, ne se met pas en forme, tronque les
 * messages longs, et sur certains navigateurs propose « ne plus afficher ce
 * type de message » — case qui, une fois cochée, fait accepter silencieusement
 * toutes les confirmations suivantes. Sur des gestes qui suppriment une année
 * de travail, c'est inacceptable.
 *
 * ── Pourquoi des notifications plutôt que des messages en place ─────────────
 * Un message inline sous un formulaire disparaît de l'écran dès qu'on fait
 * défiler. Une action réussie doit se voir là où l'œil se trouve, puis
 * s'effacer — sinon elle encombre.
 */

// ── Notifications ────────────────────────────────────────────────────────────

export type TonNotification = 'succes' | 'info' | 'erreur';

interface Notification {
  id: number;
  ton: TonNotification;
  message: string;
}

interface ContexteRetours {
  notifier: (message: string, ton?: TonNotification) => void;
  /** Résout à `true` si l'utilisateur confirme. */
  confirmer: (demande: DemandeConfirmation) => Promise<boolean>;
}

export interface DemandeConfirmation {
  titre: string;
  message: ReactNode;
  /** Libellé du bouton d'action. Doit DIRE l'action : « Supprimer », pas « OK ». */
  libelleConfirmation?: string;
  /** Rouge, pour les gestes destructeurs. */
  destructeur?: boolean;
  /**
   * Exige de recopier ce texte avant d'activer le bouton.
   *
   * Réservé à l'irréversible : une case se coche par réflexe, recopier le nom
   * de sa propre école demande de s'arrêter une seconde.
   */
  saisieAttendue?: string;
}

const Contexte = createContext<ContexteRetours | null>(null);

/** Durée d'affichage. Une erreur reste plus longtemps : elle demande à être lue. */
const DUREES: Record<TonNotification, number> = {
  succes: 4000,
  info: 5000,
  erreur: 9000,
};

export function FournisseurRetours({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [confirmation, setConfirmation] = useState<
    (DemandeConfirmation & { resoudre: (ok: boolean) => void }) | null
  >(null);
  const [saisie, setSaisie] = useState('');
  const compteur = useRef(0);

  const notifier = useCallback((message: string, ton: TonNotification = 'succes') => {
    const id = ++compteur.current;
    setNotifications((liste) => [...liste, { id, ton, message }]);
    setTimeout(
      () => setNotifications((liste) => liste.filter((n) => n.id !== id)),
      DUREES[ton],
    );
  }, []);

  const confirmer = useCallback((demande: DemandeConfirmation) => {
    setSaisie('');
    return new Promise<boolean>((resoudre) => {
      setConfirmation({ ...demande, resoudre });
    });
  }, []);

  const repondre = useCallback((ok: boolean) => {
    setConfirmation((actuelle) => {
      actuelle?.resoudre(ok);
      return null;
    });
    setSaisie('');
  }, []);

  const valeur = useMemo(() => ({ notifier, confirmer }), [notifier, confirmer]);

  const saisieValide = confirmation?.saisieAttendue
    ? saisie.trim() === confirmation.saisieAttendue.trim()
    : true;

  return (
    <Contexte.Provider value={valeur}>
      {children}

      {/*
        * `role="status"` et `aria-live="polite"` : annoncé aux lecteurs
        * d'écran sans interrompre la saisie en cours. Une notification de
        * succès n'est jamais assez urgente pour couper la parole.
        */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 print:hidden"
      >
        {notifications.map((n) => (
          <div
            key={n.id}
            className={[
              'pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 text-sm',
              'shadow-[var(--ombre-flottante)] motion-safe:animate-[glisser_.18s_ease-out]',
              n.ton === 'erreur'
                ? 'border-[var(--color-danger)] bg-[var(--color-danger-soft)] text-[var(--color-danger)]'
                : n.ton === 'info'
                  ? 'border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-fg)]'
                  : 'border-[var(--color-success)] bg-[var(--color-success-soft)] text-[var(--color-success)]',
            ].join(' ')}
          >
            <span className="flex-1">{n.message}</span>
            <button
              type="button"
              onClick={() => setNotifications((l) => l.filter((x) => x.id !== n.id))}
              aria-label="Fermer la notification"
              className="shrink-0 opacity-60 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {confirmation && (
        <DialogueConfirmation
          demande={confirmation}
          saisie={saisie}
          surSaisie={setSaisie}
          saisieValide={saisieValide}
          surReponse={repondre}
        />
      )}
    </Contexte.Provider>
  );
}

function DialogueConfirmation({
  demande, saisie, surSaisie, saisieValide, surReponse,
}: {
  demande: DemandeConfirmation;
  saisie: string;
  surSaisie: (v: string) => void;
  saisieValide: boolean;
  surReponse: (ok: boolean) => void;
}) {
  const reference = useRef<HTMLDialogElement>(null);
  const declencheur = useRef<Element | null>(null);

  useEffect(() => {
    declencheur.current = document.activeElement;
    reference.current?.showModal();
    return () => (declencheur.current as HTMLElement | null)?.focus?.();
  }, []);

  return (
    <dialog
      ref={reference}
      // Échap annule : c'est le geste de sortie attendu, et il doit toujours
      // valoir « non », jamais « oui ».
      onCancel={(e) => { e.preventDefault(); surReponse(false); }}
      // Fermée par le navigateur sans réponse : c'est un « non », et l'écran
      // doit l'apprendre, sinon la question reste pendante à jamais.
      onClose={() => surReponse(false)}
      /*
       * Le clic sur le fond ne répond RIEN.
       *
       * Ailleurs dans l'application, cliquer à côté d'une boîte ne la ferme
       * pas ; ici, cela valait « non » en silence. Une même question doit
       * appeler le même geste, et une réponse se donne exprès.
       */
      aria-labelledby="titre-confirmation"
      className={[
        // `m-auto` : centrage natif, supprimé par la remise à zéro de Tailwind.
        'm-auto w-[min(30rem,calc(100vw-2rem))] rounded-xl p-0 backdrop:bg-black/50',
        'border border-[var(--color-border)] bg-[var(--color-surface)]',
        'text-[var(--color-fg)] shadow-[var(--ombre-flottante)]',
        /*
         * ── Le message n'est pas toujours une phrase écrite par nous ────────
         *
         * « Retirer ce bloc ? » cite le bloc, et un bloc de programmation
         * contient ce que l'enseignant y a mis : dix mots, ou quinze lignes.
         * Sans hauteur maximale, la boîte grandissait jusqu'à pousser ses
         * propres boutons hors de l'écran — on ne pouvait plus ni confirmer,
         * ni annuler autrement qu'avec Échap.
         *
         * La hauteur est donc bornée, et c'est le CORPS qui défile :
         * l'en-tête et les boutons restent visibles. `overflow-hidden` tient
         * les angles arrondis, que le corps déborderait sinon.
         */
        'flex max-h-[calc(100dvh-4rem)] flex-col overflow-hidden',
      ].join(' ')}
    >
      <header className="shrink-0 border-b border-[var(--color-border)] px-5 py-4">
        <h2 id="titre-confirmation" className="text-base font-semibold">
          {demande.titre}
        </h2>
      </header>

      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-5 py-4 text-sm">
        {/*
          * `whitespace-pre-wrap break-words` : un contenu cité garde ses
          * retours à la ligne, et un mot sans espace — une adresse collée, un
          * mot-clé à rallonge — se coupe au lieu d'élargir la boîte.
          */}
        <div className="whitespace-pre-wrap break-words text-[var(--color-fg-muted)]">
          {demande.message}
        </div>

        {demande.saisieAttendue && (
          <label className="flex flex-col gap-1.5">
            <span className="font-medium text-[var(--color-fg)]">
              {`Pour confirmer, saisissez « ${demande.saisieAttendue} »`}
            </span>
            <input
              value={saisie}
              onChange={(e) => surSaisie(e.target.value)}
              autoFocus
              className={[
                'rounded-lg border px-3 py-2 text-sm',
                'border-[var(--color-border-strong)] bg-[var(--color-surface-input)]',
                'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
              ].join(' ')}
            />
          </label>
        )}
      </div>

      <footer className="flex shrink-0 justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">
        <Bouton variante="ghost" onClick={() => surReponse(false)}>Annuler</Bouton>
        <Bouton
          variante={demande.destructeur ? 'danger' : 'primary'}
          disabled={!saisieValide}
          onClick={() => surReponse(true)}
          // Focus par défaut sur l'action SEULEMENT si elle n'est pas
          // destructrice : sur un geste irréversible, la touche Entrée ne doit
          // pas suffire.
          autoFocus={!demande.destructeur && !demande.saisieAttendue}
        >
          {demande.libelleConfirmation ?? 'Confirmer'}
        </Bouton>
      </footer>
    </dialog>
  );
}

export function useRetours(): ContexteRetours {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error('useRetours doit être utilisé dans un FournisseurRetours');
  return contexte;
}
